// External globals
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue } from '@/utils/legacy-utils';
import { hasNumericValue } from '@/utils/validation';

// MDT Module Entry Modal — React bridge
// The Vue component definition has been removed; the React equivalent
// (MdtModuleEntryModalReact) is registered on window by the React bundle.

export interface MdtModuleEntryData {
    callSign: string | null;
    companyVehicleId: number | null;
    availableCompanyVehicles: Array<{ id: number, vehicleName: string }>;
    availableCallSigns: Array<{ id: number, callSign: string }> | null;
}

export interface MdtModuleEntryCallbacks {
    onConfirm: (callSign: string | null, availableVehicleId: number | null) => void;
}

export let _mdtModuleEntryContainer: HTMLElement | null = null;
export let _mdtModuleEntryIsOpen = false;

// Helper functions to show/hide the modal
export function showMdtModuleEntryModal(data: MdtModuleEntryData, callbacks: MdtModuleEntryCallbacks) {
    // Get or create the mount point
    if (!_mdtModuleEntryContainer) {
        _mdtModuleEntryContainer = document.createElement('div');
        _mdtModuleEntryContainer.id = 'mdtModuleEntryModalMount';
        document.body.appendChild(_mdtModuleEntryContainer);
    }

    _mdtModuleEntryIsOpen = true;

    function renderModal(resetSaving?: boolean) {
        if (!_mdtModuleEntryContainer) return;

        (window as any).OrnaReact.renderComponent(
            (window as any).MdtModuleEntryModalReact,
            {
                isOpen: _mdtModuleEntryIsOpen,
                callSign: data.callSign,
                companyVehicleId: data.companyVehicleId,
                availableCompanyVehicles: data.availableCompanyVehicles || [],
                availableCallSigns: data.availableCallSigns,
                resetSaving: resetSaving || false,
                onConfirm: function (result: { callSign: string | null, companyVehicleId: number | null }) {
                    const originalCallSign = data.callSign;
                    const originalCompanyVehicleId = data.companyVehicleId;
                    const hasChanges = result.callSign !== originalCallSign || result.companyVehicleId !== originalCompanyVehicleId;

                    const finishEntry = function () {
                        App.hasMdtModuleEntryBeenConfirmed = true;
                        hideMdtModuleEntryModal();
                        callbacks.onConfirm(result.callSign, result.companyVehicleId);
                    };

                    if (hasChanges) {
                        api.updateOfficerModuleEntry(
                            { callSign: result.callSign, companyVehicleId: result.companyVehicleId },
                            function () {
                                finishEntry();
                            },
                            function () {
                                App.showPopupNotification("Failed to save changes. Please try again.", "error");
                                // Re-render with resetSaving flag to re-enable the confirm button
                                renderModal(true);
                            }
                        );
                    } else {
                        finishEntry();
                    }
                }
            },
            _mdtModuleEntryContainer
        );
    }

    renderModal();
}

export function hideMdtModuleEntryModal() {
    _mdtModuleEntryIsOpen = false;
    if (_mdtModuleEntryContainer) {
        (window as any).OrnaReact.destroyComponent(_mdtModuleEntryContainer);
        _mdtModuleEntryContainer.innerHTML = '';
    }
}

// Handler for MDT module selection - call this from module selection flow
export function handleMdtModuleSelection(result: any, proceedCallback: () => void) {
    // Check if we need to show the modal:
    // 1. Must be MDT module
    // 2. Company must have the feature enabled
    // 3. User hasn't already confirmed this session
    const shouldShowModal = result.ModuleType === "mdt" &&
        result.isMdtEntryPromptEnabled === true &&
        !App.hasMdtModuleEntryBeenConfirmed;

    if (shouldShowModal) {
        showMdtModuleEntryModal({
            callSign: result.callSign,
            companyVehicleId: result.companyVehicleId,
            availableCompanyVehicles: result.availableCompanyVehicles || [],
            availableCallSigns: result.availableCallSigns
        }, {
            onConfirm: (newCallSign: string | null, newVehicleId: number | null) => {
                proceedCallback();
            }
        });
    } else {
        // Not MDT, feature not enabled, or already confirmed this session
        proceedCallback();
    }
}


// Window registrations
(window as any).showMdtModuleEntryModal = showMdtModuleEntryModal;
(window as any).hideMdtModuleEntryModal = hideMdtModuleEntryModal;
(window as any).handleMdtModuleSelection = handleMdtModuleSelection;
(window as any).initSelectModuleScreen = typeof initSelectModuleScreen !== 'undefined' ? initSelectModuleScreen : undefined;
